/* A live miniature of a report's first page for the Reports gallery cards:
 * the canonical TV renderer (/tv-preview/preview.html, same as the TVs,
 * workspace and viewer) loaded at a fixed 1920x1080 and CSS-scaled to the
 * card, static (animations=0) and non-interactive.
 *
 * Cheap on a big gallery: a frame only loads once its card scrolls into view,
 * at most MAX_LOADING frames load at the same time, and the placeholder stays
 * up until the renderer reports the page rendered (or it fails / times out). */
import { useEffect, useRef, useState, type ReactNode } from "react";

import { PREVIEW_STAGE_BG, usePreviewTheme } from "../shell/TvFrameTheme";

const STAGE_W = 1920;
const STAGE_H = 1080;
const MAX_LOADING = 3;
const LOAD_TIMEOUT_MS = 25000;

let active = 0;
const waiting: Array<() => void> = [];

/** Resolves with a release function once a loading slot is free. */
function acquireSlot(): { ready: Promise<void>; release: () => void; cancel: () => void } {
  let released = false;
  let granted = false;
  let wake: (() => void) | null = null;
  const ready = new Promise<void>((resolve) => {
    wake = () => {
      granted = true;
      active++;
      resolve();
    };
    if (active < MAX_LOADING) wake();
    else waiting.push(wake);
  });
  const release = () => {
    if (released) return;
    released = true;
    if (!granted) {
      const i = waiting.indexOf(wake!);
      if (i >= 0) waiting.splice(i, 1);
      return;
    }
    active--;
    waiting.shift()?.();
  };
  return { ready, release, cancel: release };
}

export function ReportPreviewThumb({
  reportId,
  source,
  placeholder,
  title,
  bleed = true,
  unloadOffscreen = false,
}: {
  reportId: string;
  source: "admin" | "owner" | "published";
  placeholder: ReactNode;
  title: string;
  /** Stretch over the Card's padding (gallery cards). Off for compact tiles. */
  bleed?: boolean;
  /** Drop the frame again once it scrolls far away -- for views that repeat
   * the same report many times (the per-screen view). */
  unloadOffscreen?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const [visible, setVisible] = useState(false);
  const [load, setLoad] = useState(false);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "failed">("idle");
  const [scale, setScale] = useState(0);
  const [theme] = usePreviewTheme();

  // Scale the 1920x1080 stage to whatever width the card has.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / STAGE_W));
    ro.observe(el);
    setScale(el.clientWidth / STAGE_W);
    return () => ro.disconnect();
  }, []);

  // Start once the card is (nearly) on screen. Normally never unload after
  // that; with unloadOffscreen, unload once it is far out of view again.
  useEffect(() => {
    const el = box.current;
    if (!el || (visible && !unloadOffscreen)) return;
    const io = new IntersectionObserver((entries) => {
      const on = entries.some((e) => e.isIntersecting);
      if (on) {
        setVisible(true);
        if (!unloadOffscreen) io.disconnect();
      } else if (unloadOffscreen) {
        setVisible(false);
        setLoad(false);
        setState("idle");
      }
    }, { rootMargin: unloadOffscreen ? "600px" : "200px" });
    io.observe(el);
    return () => io.disconnect();
  }, [visible, unloadOffscreen]);

  // Wait for a loading slot, then mount the frame; free the slot on
  // rendered / error / timeout / unmount.
  useEffect(() => {
    if (!visible) return;
    const slot = acquireSlot();
    let alive = true;
    let timer: number | undefined;
    const finish = (ok: boolean) => {
      if (!alive) return;
      window.clearTimeout(timer);
      slot.release();
      setState((s) => (s === "ready" ? s : ok ? "ready" : "failed"));
    };
    const onMsg = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return;
      const t = (e.data as { type?: string })?.type;
      if (t === "mrp:rendered") finish(true);
      else if (t === "mrp:error") finish(false);
    };
    window.addEventListener("message", onMsg);
    slot.ready.then(() => {
      if (!alive) return;
      setLoad(true);
      setState("loading");
      timer = window.setTimeout(() => finish(false), LOAD_TIMEOUT_MS);
    });
    return () => {
      alive = false;
      window.clearTimeout(timer);
      window.removeEventListener("message", onMsg);
      slot.cancel();
    };
  }, [visible, reportId, source]);

  const params = new URLSearchParams({ reportId, mode: source, embed: "1", theme, animations: "0" });
  const shown = state === "ready";

  return (
    <div
      ref={box}
      aria-label={`Preview of ${title}`}
      style={{
        aspectRatio: `${STAGE_W} / ${STAGE_H}`,
        margin: bleed ? "-16px -20px 14px" : 0,
        borderRadius: bleed ? "10px 10px 0 0" : 0,
        position: "relative",
        overflow: "hidden",
        background: PREVIEW_STAGE_BG[theme],
        borderBottom: "1px solid var(--border, rgba(0,0,0,0.08))",
      }}
    >
      <div style={{ position: "absolute", inset: 0, opacity: shown ? 0 : 1, transition: "opacity 250ms ease" }}>{placeholder}</div>
      {load && scale > 0 ? (
        <iframe
          ref={frame}
          title={`Preview of ${title}`}
          src={`/tv-preview/preview.html?${params.toString()}`}
          tabIndex={-1}
          aria-hidden
          loading="lazy"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: STAGE_W,
            height: STAGE_H,
            border: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            pointerEvents: "none",
            opacity: shown ? 1 : 0,
            transition: "opacity 250ms ease",
          }}
        />
      ) : null}
    </div>
  );
}
